import type {
  BootInfo,
  JoinCollaborationParams,
  ErrorInfo,
  HostInfo,
  JoinPresentationParams,
  StartPresentationParams,
} from "./type";
import { Action } from "./type";
import { isPossiblyURL, mdcppFetchAccessToken } from "./utility";
import {
  bootApplication,
  createAdaptiveLogger,
  endSessionFromHost,
  getAbsoluteTimeSinceEpoch,
  notifyHostAccessibilityLoopComplete,
  LogCategory,
  TraceLevel,
  DocRebootSupportMode,
  getRemoteUlsEndpoint,
} from "@1js/owlet-sdk";

import type {
  AdaptiveLogger,
  BootApplicationParams,
  BootDiagnostics,
  SessionConfig,
  DocumentBootInfo,
  HostBootDiagnostics,
  MeetingInfo,
  WiseEnvironment,
} from "@1js/owlet-sdk";
import { v4 as uuidv4 } from "uuid";
import { PPTLiveHandler } from "./pptLive/pptLiveHandler";
import { PPTLiveEnvironment } from "./pptLive/pptLiveType";
import * as Errors from "./errors";

export let logger: AdaptiveLogger | undefined;
export let uiHostConfig: any;
export let hostClientType: string | undefined;
export let hostName: string;
export let initialized = false;
let activePPTLiveHandler: PPTLiveHandler | undefined;

const uiHostIntegrationType = "3PMeetingIntegration";

/**
 * Initializes the meeting. Must be invoked first in order to prepare the experience and perform the needed licensing checks.
 *
 * @param hostInfo - Information about the external partner host. See definition of HostInfo for details.
 * @alpha
 */
export function initializeMeeting(hostInfo: HostInfo): void {
  // do sanity check and initialize global variables
  if (!logger) {
    // 3P default environment as PRODUCTION and emit all the BULS to common endpoint
    const env: WiseEnvironment = "PRODUCTION";
    logger = createAdaptiveLogger({
      remoteUlsEndpoint: getRemoteUlsEndpoint(env),
    });
  }

  hostName = hostInfo.hostName;

  if (!uiHostConfig) {
    uiHostConfig = {
      // to do: if we need to support OneNote for 3P, we need to add the logic here and might need to change
      // the API depending on if we want to rely on internal call to detect file type or ask 3P to provide it
      // hideHeader: if file type is OneNote, hide the header
      // hideSearchInHeader: if file type is OneNote, hide the search in header
      hideAppLauncherInHeader: true,
      hideMeInHeader: true,
      hideChatInHeader: true,
      hideWorkflowInHeader: true,
      hideSettingsInHeader: true,
      showAppIconInHeader: true,
      useNeutralColorInHeader: true,
      supportsAtMentions: true,
      supportsAccessibilityLoop: true,
      supportsExcelExternalLinks: true,
      supportsCreateNewDocument: true,
      supportsExplicitUnload: true,
      supportsMoveToFolder: true,
      supportsCommonFilePicker: true,
      supportsShare: true,
      uiHostName: hostName,
      //sessionOriginPrefix: hostOrigin + ".UNIFIEDUIHOST.MEETING" + ".", // may not applicable to 3P or to do: what do we expect host to pass here? what nomenclature to follow? can tweak it in later pr
      uiHostIntegrationType,
      openLinksInNewWindow: true,
      copyLinkEnabled: true,
      supportsCustomAuthConfig: false,
      supportsContentAssembly: false,
      uiEmbed: true,
      supportsHostUiControls: true,
      supportsDirectPrint: false,
      disableOfficeAddins: true,
      supportsDocumentReboot:
        DocRebootSupportMode.SameDocOnly | DocRebootSupportMode.OtherMode,
      supportsInPlaceCreateNew: true,
      authTokenStrategy: "PairwiseBroker",
      disableEmbedViewCodeSnippet: true,
      disableCoEditInTeams: true,
    };
  }
  hostClientType = hostInfo.hostClientType ?? "web";
  initialized = true;
}

/**
 * Allows a user to join an existing Collaborate session. Must have already called initializeMeeting and startCollaboration.
 *
 * IMPORTANT: Not implemented yet.
 *
 * @param collabParams - Information needed to join the Collaborate session. See definition of JoinCollaborationParams for details.
 * @returns Information about the session. See definition of BootInfo for details.
 * @alpha
 */
export async function joinCollaboration(
  collabParams: JoinCollaborationParams
): Promise<BootInfo> {
  // do sanity check, if not initialized, return error
  if (!initialized) {
    // cannot use logger here since logger is not initialized yet
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.MeetingAPINotInitialized,
    });
  }

  const packageEntryTime = getAbsoluteTimeSinceEpoch();

  logger?.ULS.sendTraceTag(
    0x1e2da6d6 /* tag_4l01w */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    `Start join collaboration from host: ${hostName} at timestamp: ${getAbsoluteTimeSinceEpoch()}`
  );

  if (
    !collabParams.documentInfo ||
    !collabParams.sessionInfo ||
    !collabParams.container ||
    !collabParams.fetchAccessToken ||
    Object.keys(collabParams.documentInfo).length === 0 || // this is to check if the object is empty or not
    Object.keys(collabParams.sessionInfo).length === 0
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d3721 /* tag_4pt27 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " Invalid or empty input parameters"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "Invalid or empty input parameters",
      },
    });
  }

  const collabDocumentInfo = collabParams.documentInfo;

  // Handle the case when both shareUrl and documentIdentifier are null or empty
  if (!collabDocumentInfo.shareUrl && !collabDocumentInfo.documentIdentifier) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d3720 /* tag_4pt26 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`,
      },
    });
  }

  // Handle the case when an invalid shareUrl is provided.
  if (
    collabDocumentInfo.shareUrl &&
    !isPossiblyURL(collabDocumentInfo.shareUrl)
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d371f /* tag_4pt25 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " provided shareUrl is invalid"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "provided shareUrl is invalid",
      },
    });
  }

  // Handle the case when invalid documentIdentifier been provided
  if (
    collabDocumentInfo.documentIdentifier &&
    (!isPossiblyURL(collabDocumentInfo.documentIdentifier.siteUrl) ||
      collabDocumentInfo.documentIdentifier.sourceDoc === "")
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d371e /* tag_4pt24 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " siteUrl invalid or sourceDoc empty"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "siteUrl invalid or sourceDoc empty",
      },
    });
  }

  const action = collabDocumentInfo?.action ?? Action.Default; // if host does not provide action, we assign default action
  const url = new URL(window.location.href);
  const hostDomainUrl = url.origin;
  const documentIdentifier = collabDocumentInfo?.documentIdentifier;
  const docInfo: DocumentBootInfo = {
    docTenantUrl: documentIdentifier?.siteUrl, // this is the host document domain url
    action,
    sourceDoc: documentIdentifier?.sourceDoc,
    hostingPageOrigin: hostDomainUrl, // this is the host domain url to send post message back
    wdOrigin: collabDocumentInfo?.sessionOrigin,
    uiCulture: collabDocumentInfo?.uiCulture,
    dataCulture: collabDocumentInfo?.dataCulture,
    clickTime: collabDocumentInfo?.clickTime ?? new Date().getTime(),
    shareUrl: collabDocumentInfo?.shareUrl,
  };

  const meetingInfo: MeetingInfo = {
    id: collabParams.meetingInfo?.id,
    isPresenter: false,
  };

  const environment: WiseEnvironment = "PRODUCTION";
  const sessionInfo: SessionConfig = {
    hostCorrelationId: collabParams.sessionInfo?.hostCorrelationId,
    wiseEnvironment: environment, // temp make it production, later when we support sovereign, need to confirm if we can trust what 3P tells us
    meeting: meetingInfo,
    hostClientType,
    isSynthetic: collabParams.sessionInfo?.isSynthetic ?? false,
    bootDiagnostics: {
      mdcppLaunchStartTime: packageEntryTime,
    } as HostBootDiagnostics,
  };

  const param: BootApplicationParams = {
    documentBootInfo: docInfo,
    sessionConfig: sessionInfo,
    uiHostConfig,
    getAuthToken: async (resourceUrl: string, claim?: string[]) =>
      mdcppFetchAccessToken(collabParams.fetchAccessToken, resourceUrl, claim),
    eventHandlers: collabParams.eventHandlers ?? {},
    container: collabParams.container,
  };

  return bootApplication(param)
    .then((bootResult: BootDiagnostics) => {
      const bootInfo: BootInfo = {
        isBootSuccess: !!bootResult.isBootSuccess,
        // The errorInfo is included in BootInfo only when boot fails and the downstream SDK provides error details.
        ...(!bootResult.isBootSuccess && bootResult.errorInfo
          ? {
              errorInfo: {
                errorMessage: "Failed to join collaboration",
                errorType: Errors.getErrorTypeFromOWLErrorType(
                  bootResult.errorInfo.errorType
                ),
                errorName: bootResult.errorInfo.errorName,
                errorCode: bootResult.errorInfo.errorDetail?.httpErrorCode,
              },
            }
          : {}),
      };

      // ErrorInfo will always be returned if the SDK fails to join collaboration.
      if (bootResult.errorInfo) {
        // ASSERTTAG_IGNORE_START
        logger?.ULS.sendTraceTag(
          0x1e3db6a2 /* tag_4p108 */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Error,
          `Host ${hostName} failed to boot meeting with errorType: ${bootResult.errorInfo.errorType}, errorName: ${bootResult.errorInfo.errorName}, errorCode: ${bootResult.errorInfo.errorDetail?.httpErrorCode}.`
          // ASSERTTAG_IGNORE_FINISH
        );
      } else {
        logger?.ULS.sendTraceTag(
          0x1e2da6d5 /* tag_4l01v */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Important,
          `Host ${hostName} successfully booted up the meeting at timestamp: ${getAbsoluteTimeSinceEpoch()}.`
        );
      }

      return bootInfo;
    })
    .catch((error) => {
      // ASSERTTAG_IGNORE_START
      logger?.ULS.sendTraceTag(
        0x1e3db6d0 /* tag_4p11q */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `Host ${hostName} failed to boot up the meeting with error: ${error}.`
      );
      // ASSERTTAG_IGNORE_FINISH

      return {
        isBootSuccess: false,
        errorInfo: Errors.AppLoadFailedUnexpected,
      };
    });
}

/**
 * Initiates a Collaborate session. Must have already called initializeMeeting.
 *
 * IMPORTANT: Not implemented yet.
 *
 * @param collabParams - Information needed to start the Collaborate session. See definition of JoinCollaborationParams for details.
 * @returns Information about the session. See definition of BootInfo for details.
 * @alpha
 */
export async function startCollaboration(
  collabParams: JoinCollaborationParams
): Promise<BootInfo> {
  // do sanity check, if not initialized, return error
  if (!initialized) {
    // cannot use logger here since logger is not initialized yet
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.MeetingAPINotInitialized,
    });
  }

  const packageEntryTime = getAbsoluteTimeSinceEpoch();

  logger?.ULS.sendTraceTag(
    0x1e2da6d4 /* tag_4l01u */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    `Start collaboration from host: ${hostName} at timestamp: ${getAbsoluteTimeSinceEpoch()}`
  );

  if (
    !collabParams.documentInfo ||
    !collabParams.sessionInfo ||
    !collabParams.container ||
    !collabParams.fetchAccessToken ||
    Object.keys(collabParams.documentInfo).length === 0 || // this is to check if the object is empty or not
    Object.keys(collabParams.sessionInfo).length === 0
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d371d /* tag_4pt23 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " Invalid or empty input parameters"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "Invalid or empty input parameters",
      },
    });
  }

  const collabDocumentInfo = collabParams.documentInfo;

  // Handle the case when both shareUrl and documentIdentifier are null or empty
  if (!collabDocumentInfo.shareUrl && !collabDocumentInfo.documentIdentifier) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d371c /* tag_4pt22 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`
    );
    // ASSERTTAG_IGNORE_FINISH

    const errorInfo = Errors.InvalidInputParameters;
    errorInfo.errorMessage = `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`;
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: `Neither documentIdentifier nor shareUrl be passed in by host: ${hostName}.`,
      },
    });
  }

  // Handle the case when invalid shareUrl been provided
  if (
    collabDocumentInfo.shareUrl &&
    !isPossiblyURL(collabDocumentInfo.shareUrl)
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d371b /* tag_4pt21 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " provided shareUrl is invalid"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "Provided shareUrl is invalid",
      },
    });
  }

  // Handle the case when invalid documentIdentifier been provided
  if (
    collabDocumentInfo.documentIdentifier &&
    (!isPossiblyURL(collabDocumentInfo.documentIdentifier.siteUrl) ||
      collabDocumentInfo.documentIdentifier.sourceDoc === "")
  ) {
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e3d371a /* tag_4pt20 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Error,
      "host: " + hostName + " siteUrl invalid or sourceDoc empty"
    );
    // ASSERTTAG_IGNORE_FINISH

    return Promise.reject({
      isBootSuccess: false,
      errorInfo: {
        ...Errors.InvalidInputParameters,
        errorMessage: "siteUrl invalid or sourceDoc empty",
      },
    });
  }

  const action = collabDocumentInfo?.action ?? Action.Default; // if host does not provide action, we assign default action
  const url = new URL(window.location.href);
  const hostDomainUrl = url.origin;
  const docInfo: DocumentBootInfo = {
    docTenantUrl: collabDocumentInfo.documentIdentifier?.siteUrl, // this is the host document domain url
    action,
    sourceDoc: collabDocumentInfo.documentIdentifier?.sourceDoc,
    hostingPageOrigin: hostDomainUrl, // this is the host domain url to send post message back
    wdOrigin: collabDocumentInfo?.sessionOrigin,
    uiCulture: collabDocumentInfo?.uiCulture,
    dataCulture: collabDocumentInfo?.dataCulture,
    clickTime: collabDocumentInfo?.clickTime ?? new Date().getTime(),
    shareUrl: collabDocumentInfo?.shareUrl,
  };

  const meetingInfo: MeetingInfo = {
    id: collabParams.meetingInfo?.id,
    isPresenter: true,
  };

  const environment: WiseEnvironment = "PRODUCTION";

  const sessionInfo: SessionConfig = {
    hostCorrelationId: collabParams.sessionInfo?.hostCorrelationId,
    wiseEnvironment: environment, // temp make it production, later when we support sovereign, need to confirm if we can trust what 3P tells us
    meeting: meetingInfo,
    hostClientType,
    isSynthetic: collabParams.sessionInfo?.isSynthetic ?? false,
    bootDiagnostics: {
      mdcppLaunchStartTime: packageEntryTime,
    } as HostBootDiagnostics,
  };

  const param: BootApplicationParams = {
    documentBootInfo: docInfo,
    sessionConfig: sessionInfo,
    uiHostConfig,
    getAuthToken: async (resourceUrl: string, claim?: string[]) =>
      mdcppFetchAccessToken(collabParams.fetchAccessToken, resourceUrl, claim),
    eventHandlers: collabParams.eventHandlers ?? {},
    container: collabParams.container,
  };

  return bootApplication(param)
    .then((bootResult: BootDiagnostics) => {
      const bootInfo: BootInfo = {
        isBootSuccess: !!bootResult.isBootSuccess,
        // The errorInfo is included in BootInfo only when boot fails and the downstream SDK provides error details.
        ...(!bootResult.isBootSuccess && bootResult.errorInfo
          ? {
              errorInfo: {
                errorMessage: "Failed to start collaboration",
                errorType: Errors.getErrorTypeFromOWLErrorType(
                  bootResult.errorInfo.errorType
                ),
                errorName: bootResult.errorInfo.errorName,
                errorCode: bootResult.errorInfo.errorDetail?.httpErrorCode,
              },
            }
          : {}),
      };

      // ErrorInfo will always be returned if the SDK fails to start collaboration.
      if (bootResult.errorInfo) {
        // ASSERTTAG_IGNORE_START
        logger?.ULS.sendTraceTag(
          0x1e3db6a1 /* tag_4p107 */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Error,
          `Host ${hostName} failed to boot up the meeting with errorType: ${bootResult.errorInfo.errorType}, errorName: ${bootResult.errorInfo.errorName}, errorCode: ${bootResult.errorInfo.errorDetail?.httpErrorCode}.`
        );
        // ASSERTTAG_IGNORE_FINISH
      } else {
        logger?.ULS.sendTraceTag(
          0x1e2da6d3 /* tag_4l01t */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Important,
          `Host ${hostName} successfully booted up the meeting at timestamp: ${getAbsoluteTimeSinceEpoch()}.`
        );
      }

      return bootInfo;
    })
    .catch((error) => {
      // ASSERTTAG_IGNORE_START
      logger?.ULS.sendTraceTag(
        0x1e3db6cf /* tag_4p11p */,
        LogCategory.msoulscat_Wac_UnifiedUiHost,
        TraceLevel.Error,
        `Host ${hostName} failed to boot up the meeting with error: ${error}.`
      );
      // ASSERTTAG_IGNORE_FINISH

      return {
        isBootSuccess: false,
        errorInfo: Errors.AppLoadFailedUnexpected,
      };
    });
}

// this API is used for 3P host to end a session
/**
 * Signals the end of the Collaborate session.
 *
 * IMPORTANT: Not implemented yet.
 *
 * @returns Pending...
 * @alpha
 */
export function stopCollaboration(): Promise<string> {
  // ASSERTTAG_IGNORE_START
  logger?.ULS.sendTraceTag(
    0x1e3db6ce /* tag_4p11o */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    "endSession called from host: " + hostName
  );
  // ASSERTTAG_IGNORE_FINISH

  return endSessionFromHost(); // need to check the type? what does the string look like?
}

// this API is used for 3P host to notify Microsoft that accessibility loop is complete
/**
 * Notify the Live or Collaborate experience how to handle keyboard events related to focus change.
 *
 * @param navigateForward - Indicates the keyboard focus direction when the user switches between the Office app iframe
 * and the external partner host UI frame. `true` represents clockwise, otherwise `false`.
 * @returns A boolean value indicating whether the loop has completed successfully.
 * @alpha
 */
export function accessibilityLoopComplete(navigateForward: boolean): boolean {
  // ASSERTTAG_IGNORE_START
  logger?.ULS.sendTraceTag(
    0x1e3db6cd /* tag_4p11n */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Important,
    "hostAccessibilityLoopComplete called from host: " + hostName
  );
  // ASSERTTAG_IGNORE_FINISH

  return notifyHostAccessibilityLoopComplete(navigateForward);
}

/**
 * Initiates a new Present session. Must have already called initializeMeeting.
 *
 * @param startParams - Information needed to start the new Present session. See definition of StartPresentationParams for details.
 * @returns Information about the session. See definition of BootInfo for details.
 *
 * @remarks
 * Starts a new Present sharing session for a document and renders the presenter's view into the specified container element. If the
 * presentation starts successfully, the response will contain a `joinInfo` parameter. This must be passed to the attendee for them
 * to join the existing Present session. `joinInfo` contains secret info and must not be included in any telemetry or diagnostic logs.
 *
 * @alpha
 */
export async function startPresentation(
  presParams: StartPresentationParams
): Promise<BootInfo> {
  if (!initialized || logger === undefined) {
    // cannot use logger here since logger is not initialized yet
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.MeetingAPINotInitialized,
    });
  }

  if (activePPTLiveHandler !== undefined) {
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.PresentationInProgress,
    });
  }

  const url = new URL(window.location.href);
  const pageDomain = url.origin;

  const instanceId = uuidv4();
  activePPTLiveHandler = new PPTLiveHandler(
    logger.ULS,
    instanceId,
    PPTLiveEnvironment.dev,
    (error: ErrorInfo) => {
      if (presParams.eventHandlers?.onError) {
        presParams.eventHandlers?.onError(error);
      }
    }
  );

  const presDocumentIdentifier = presParams.documentInfo.documentIdentifier;
  const documentUrl = new URL(presDocumentIdentifier?.siteUrl ?? ""); // siteUrl is expected to always be present here, undefined scenario should not occur."
  const accessToken = await presParams.fetchAccessToken(
    `https://${documentUrl.hostname}`,
    [".default"]
  );

  const response = await activePPTLiveHandler.present(
    presParams.container,
    presParams.documentInfo,
    presParams.meetingInfo,
    hostName,
    pageDomain,
    accessToken
  );

  return {
    isBootSuccess: response.success,
    joinInfo: response.joinInfo,
    errorInfo: response.error,
  };
}

/**
 * Allows an attendee to join an existing Present session. Must have already called initializeMeeting and startPresentation.
 *
 * @param presParams - Information needed to join the existing Present session. See definition of JoinPresentationParams for details.
 * @returns Information about the session. See definition of BootInfo for details.
 *
 * @remarks
 * Use the `joinInfo` property returned in the object returned by the startPresentation call that initialized the sharing session.
 *
 * @alpha
 */
export async function joinPresentation(
  presParams: JoinPresentationParams
): Promise<BootInfo> {
  if (!initialized || logger === undefined) {
    // cannot use logger here since logger is not initialized yet
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.MeetingAPINotInitialized,
    });
  }

  if (activePPTLiveHandler !== undefined) {
    return Promise.reject({
      isBootSuccess: false,
      errorInfo: Errors.PresentationInProgress,
    });
  }

  const url = new URL(window.location.href);
  const pageDomain = url.origin;

  const instanceId = uuidv4();
  activePPTLiveHandler = new PPTLiveHandler(
    logger.ULS,
    instanceId,
    PPTLiveEnvironment.dev,
    (error: ErrorInfo) => {
      if (presParams.eventHandlers?.onError) {
        presParams.eventHandlers?.onError(error);
      }
    }
  );

  const response = await activePPTLiveHandler.join(
    presParams.container,
    presParams.joinInfo,
    presParams.meetingInfo,
    hostName,
    pageDomain
  );

  return {
    isBootSuccess: response.success,
    errorInfo: response.error,
  };
}

/**
 * Signals the end of the Present session. Shuts down the app and removes the iframe from its parent container.
 * Must call stopPresentation before you can call startPresentation or joinPresentation again.
 * Only one active presentation is supported at one time.
 *
 * @returns `true` if the presentation was successfully stopped, otherwise `false`.
 * @alpha
 */
export async function stopPresentation(): Promise<boolean> {
  if (activePPTLiveHandler === undefined) {
    return true;
  }

  // dispose is not yet an async function, but it could be in the future
  // if we need to send a 'close' message to the app. We are defining
  // stopPresentation as async so we can change this in the future without
  // updating the API contract.
  activePPTLiveHandler?.dispose();
  activePPTLiveHandler = undefined;
  return true;
}
